import {
  captureStateSnapshot,
  formatSnapshot,
  exportSnapshots,
  type StateSnapshot,
} from "../debug/state-snapshot";

declare global {
  interface Window {
    __debugWired?: boolean;
    dumpDebugState?: () => Promise<StateSnapshot>;
    captureStateSnapshot?: typeof captureStateSnapshot;
    formatSnapshot?: typeof formatSnapshot;
    exportSnapshots?: typeof exportSnapshots;
    stateSnapshots?: StateSnapshot[];
    consoleLogs?: Array<{ level: string; args: unknown[]; timestamp: string }>;
    downloadDebugMini?: () => void;
    snap?: (label?: string) => Promise<StateSnapshot>;
    exportStory?: () => string;
    downloadStory?: () => Promise<void>;
    downloadMiniStory?: () => void;
  }
}

export function setupDebugTools() {
  if (window.__debugWired) return;
  window.__debugWired = true;

  window.dumpDebugState = () => captureStateSnapshot();
  window.captureStateSnapshot = captureStateSnapshot;
  window.formatSnapshot = formatSnapshot;
  window.exportSnapshots = exportSnapshots;
  window.stateSnapshots = [];
  window.consoleLogs = [];

  const originalLog = console.log;
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalInfo = console.info;

  const captureLog =
    (level: string) =>
    (...args: unknown[]) => {
      window.consoleLogs!.push({
        level,
        args,
        timestamp: new Date().toISOString(),
      });
      if (level === "error") originalError(...args);
      else if (level === "warn") originalWarn(...args);
      else if (level === "info") originalInfo(...args);
      else originalLog(...args);
    };

  console.log = captureLog("log");
  console.error = captureLog("error");
  console.warn = captureLog("warn");
  console.info = captureLog("info");

  window.snap = async (label?: string) => {
    const snapshot = await captureStateSnapshot(label);
    (snapshot as any).consoleLogs = (window.consoleLogs ?? []).slice(-50);
    (window.stateSnapshots ??= []).push(snapshot);
    if (import.meta.env.VITE_DEBUG_SNAPSHOTS) console.log(formatSnapshot(snapshot));
    return snapshot;
  };

  window.exportStory = () => {
    const story = exportSnapshots(window.stateSnapshots ?? []);
    if (import.meta.env.VITE_DEBUG_SNAPSHOTS) console.log(story);
    return story;
  };

  window.downloadStory = async () => {
    const data = {
      timestamp: new Date().toISOString(),
      snapshots: window.stateSnapshots ?? [],
      consoleLogs: window.consoleLogs ?? [],
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `retreat-debug-full-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  window.downloadMiniStory = () => {
    const minimal = (window.stateSnapshots ?? []).map((s: any) => ({
      t: s.timestamp,
      l: s.label,
      e: s.engineState,
      s: s.currentSession
        ? { id: s.currentSession.retreatId, offset: s.currentSession.lastOffset }
        : null,
      ls: Object.keys(s.localStorage ?? {}).length,
      ss: Object.keys(s.sessionStorage ?? {}).length,
      idb: Object.keys(s.indexedDB ?? {}).length,
      logs:
        s.consoleLogs
          ?.slice(-10)
          .map((log: any) => `[${log.level}] ${JSON.stringify(log.args).slice(0, 100)}`) ?? [],
    }));
    const blob = new Blob([JSON.stringify({ timestamp: new Date().toISOString(), snapshots: minimal }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `retreat-debug-mini-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  window.downloadDebugMini = () => {
    const minimal = (window.stateSnapshots || []).map((s) => ({
      timestamp: (s as any).timestamp,
      engineState: (s as any).snapshot?.engine?.snapshot?.status,
      sessionElapsed: (s as any).snapshot?.engine?.snapshot?.elapsedSeconds,
      hasPendingSession: !!(s as any).snapshot?.engine?.snapshot
        ?.pendingSession,
      logs:
        window.consoleLogs
          ?.filter(
            (log) =>
              new Date(log.timestamp).getTime() >=
                new Date((s as any).timestamp).getTime() - 1000 &&
              new Date(log.timestamp).getTime() <=
                new Date((s as any).timestamp).getTime() + 1000,
          )
          .map(
            (log) => `[${log.level}] ${JSON.stringify(log.args).slice(0, 100)}`,
          ) || [],
    }));

    const data = {
      timestamp: new Date().toISOString(),
      snapshots: minimal,
    };

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `retreat-debug-mini-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
}
